import React from "react";
import ReactDOM from "react-dom";

import Portal from "../../atoms/Portal";
import { ReactComponent as SuccessIcon } from "../../assets/svg/success.svg";
import { ReactComponent as ErrorIcon } from "../../assets/svg/error.svg";

import styles from "./Toast.module.scss";

export type ToastProps = {
  onClose?: (...args: any[]) => void;
  position?: "topRight" | "bottomRight";
  autoClose?: number;
  container?: Element;
};

const Success: React.FC<ToastProps> = ({
  onClose = (...args: any[]) => {},
  autoClose = 3000,
  position = "topRight",
  container,
  children,
}) => {
  const [isClose, setIsClose] = React.useState(false);

  React.useEffect(() => {
    const timerId = setTimeout(() => setIsClose(true), autoClose);

    return () => {
      clearTimeout(timerId);
    };
  }, [autoClose]);

  const animationEnd = () => {
    if (isClose) onClose();
  };

  return (
    <Portal container={container}>
      <div
        className={`
          ${styles.toast}
          ${styles[position]}
          ${styles.success}
          ${isClose ? styles.close : styles.open}
        `}
        onAnimationEnd={animationEnd}
      >
        <div className={styles.toastIcon}>
          <SuccessIcon />
        </div>
        {children}
      </div>
    </Portal>
  );
};

const Error: React.FC<ToastProps> = ({
  onClose = (...args: any[]) => {},
  autoClose = 3000,
  position = "topRight",
  container,
  children,
}) => {
  const [isClose, setIsClose] = React.useState(false);

  React.useEffect(() => {
    const timerId = setTimeout(() => setIsClose(true), autoClose);

    return () => {
      clearTimeout(timerId);
    };
  }, [autoClose]);

  const animationEnd = () => {
    if (isClose) onClose();
  };

  return (
    <Portal container={container}>
      <div
        className={`
          ${styles.toast}
          ${styles[position]}
          ${styles.error}
          ${isClose ? styles.close : styles.open}
        `}
        onAnimationEnd={animationEnd}
      >
        <div className={styles.toastIcon}>
          <ErrorIcon />
        </div>
        {children}
      </div>
    </Portal>
  );
};

function renderToast({
  component: Component,
  onClose = (...args: any[]) => {},
  ...rest
}: { component: React.ElementType; children: React.ReactNode } & ToastProps) {
  const el = document.createElement("div");
  document.body.appendChild(el);

  ReactDOM.render(
    <Component
      container={el}
      onClose={() => {
        document.body.removeChild(el);
        onClose();
      }}
      {...rest}
    />,
    el,
  );
}

export default {
  Success,
  Error,
  success(children: React.ReactNode, options?: ToastProps) {
    return renderToast({ component: this.Success, children, ...options });
  },
  error(children: React.ReactNode, options?: ToastProps) {
    return renderToast({ component: this.Error, children, ...options });
  },
};
